import { useState, useEffect, useCallback } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { UserMenu } from './UserMenu';
import { EmailVerificationBanner } from '@/components/EmailVerificationBanner';
import { useAuth } from '@/contexts/AuthContext';
import { useCredits } from '@/hooks/useCredits';
import { PageContextProvider } from '@/contexts/PageContext';
import {
  DebateDrawer,
  DebateMinimizedPill,
  DebateTriggerFAB,
  type DebateDrawerMode,
} from '@/components/debate/DebateDrawer';
import { useDebateStream } from '@/hooks/useDebateStream';

export function DashboardLayout() {
  // Start expanded on desktop; mobile uses the overlay menu.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { user, logout } = useAuth();
  const { balance, isUnlimited } = useCredits();
  const location = useLocation();

  // Debate drawer state — lives here so it survives page navigation
  const [debateDrawerMode, setDebateDrawerMode] = useState<DebateDrawerMode>('closed');

  const debateStream = useDebateStream();
  const debateIsActive = debateStream.state.status !== 'idle';

  // Keyboard shortcut: Ctrl+Shift+D to toggle debate panel
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        setDebateDrawerMode((prev) => {
          if (prev === 'open') {
            // Match Escape/backdrop behavior: minimize if debate active, close if idle
            return debateStream.state.status !== 'idle' ? 'minimized' : 'closed';
          }
          return 'open';
        });
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [debateStream.state.status]);

  const openDebateDrawer = useCallback(() => setDebateDrawerMode('open'), []);
  const closeDebateDrawer = useCallback(() => setDebateDrawerMode('closed'), []);
  const minimizeDebateDrawer = useCallback(() => setDebateDrawerMode('minimized'), []);

  // Data-dense pages get a wider container
  const wideRoutes = ['/dashboard/portfolio', '/preferences'];
  const isWideRoute = wideRoutes.some(r => location.pathname.startsWith(r));

  // Full-bleed pages manage their own padding and scroll
  const isFullBleed = false;

  const widthClass = isWideRoute ? 'max-w-[1800px]' : sidebarCollapsed ? 'max-w-6xl' : 'max-w-5xl';

  return (
    <PageContextProvider>
      <div className="flex h-screen bg-background">
        <Sidebar
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
        <main className="relative flex-1 flex flex-col overflow-hidden">
          {/* User menu — top-right, absolutely positioned so it doesn't consume flow space */}
          {user && (
            <div className="absolute top-0 right-0 z-30 flex justify-end px-4 pt-3 pb-0 md:px-6 pointer-events-none">
              <div className="pointer-events-auto">
                <UserMenu
                  user={user}
                  onLogout={logout}
                  creditBalance={balance}
                  isUnlimited={isUnlimited}
                />
              </div>
            </div>
          )}
          {/* Content area */}
          {isFullBleed ? (
            <div className={`flex-1 min-h-0 flex flex-col overflow-hidden mx-auto w-full ${widthClass}`}>
              <EmailVerificationBanner />
              <Outlet />
            </div>
          ) : (
            <div className="flex-1 min-h-0 overflow-y-auto">
              <div className={`mx-auto px-4 py-6 pt-20 md:pt-2 md:px-6 lg:px-8 ${widthClass}`}>
                <EmailVerificationBanner />
                <Outlet />
              </div>
            </div>
          )}
        </main>

        {/* Global Debate System — persists across page navigation */}
        <DebateDrawer
          drawerMode={debateDrawerMode}
          onClose={closeDebateDrawer}
          onMinimize={minimizeDebateDrawer}
          onOpen={openDebateDrawer}
          debateStream={debateStream}
        />

        {/* Debate trigger: show pill when active + drawer not open, FAB otherwise */}
        {debateDrawerMode !== 'open' && debateIsActive ? (
          <DebateMinimizedPill state={debateStream.state} onClick={openDebateDrawer} />
        ) : debateDrawerMode !== 'open' ? (
          <DebateTriggerFAB onClick={openDebateDrawer} />
        ) : null}
      </div>
    </PageContextProvider>
  );
}
